import React from 'react'
import { useTranslation } from 'react-i18next'

import { BlockTypes } from 'enum/block-type.enum'
import { EvaluationStatesEnum } from 'enum/evaluation-states.enum'
import { ILessonContent } from 'store/states/lessons.state'
import { sanitizeHtml } from 'helpers/sanitizeHtml'
import Multimedia from '../components/multimedia/Multimedia'
import RichText from '../components/rich-text/RichText'
import { Survey } from '../components/survey/Survey'
import Task from '../components/task/Task'
import EvaluationAlert from '../components/evaluation/components/evaluation-alert/EvaluationAlert'
import { PrepareEvaluation } from '../components/evaluation/ContentBlockEvaluation'
import Scorm from '../components/Scorm'
import { Meeting } from '../components/Meeting'
import { MandatoryBlock } from './MandatoryBlock'
import styles from './GenerateContent.module.scss'

interface GenerateContentProps {
  lessonId: number
  lessonContent: ILessonContent[]
  refArray: React.MutableRefObject<any[]>
  canEdit: boolean
  count: React.MutableRefObject<number>
  setOnEvaluation: any
  location: any
  reload: () => void
  next?: () => void
  courseId: number
}

export const GenerateContent = ({
  lessonId,
  lessonContent,
  refArray,
  canEdit,
  count,
  setOnEvaluation,
  location,
  reload,
  next,
  courseId,
}: GenerateContentProps) => {
  const { t } = useTranslation()
  const content = [] as any
  count.current = 0
  lessonContent.map((item, index: number) => {
    const key = item.id + '-' + item.type

    switch (item.type) {
      case BlockTypes.BlockSurvey:
        {
          const mandatory = item.questionsForms.some(
            (question) => question.mandatory
          )
          content.push(
            <MandatoryBlock key={key} content={item} isMandatory={mandatory} />
          )
          content.push(
            <Survey
              key={key}
              id={item.id}
              questionList={item.questionsForms}
              canEdit={canEdit}
              refArray={refArray}
              count={count}
              lessonIndex={index}
            />
          )
        }
        break
      case BlockTypes.BlockMeeting:
        content.push(<Meeting key={key} data={item} />)
        break
      case BlockTypes.BlockRichText:
        content.push(
          <div className="mb-4" key={key}>
            <RichText data={item}>
              <div className={styles.title}>{item.name}</div>
            </RichText>
          </div>
        )
        break
      case BlockTypes.BlockTask:
        content.push(
          <MandatoryBlock
            key={key}
            content={item}
            isMandatory={item.mandatory}
          />
        )
        content.push(
          <div
            className="mb-4"
            key={
              'blockTask/' +
              item.id +
              '-' +
              item.blockInstanceableId +
              '-' +
              index
            }
          >
            <Task
              id={item.id}
              data={item}
              canEdit={canEdit}
              refArray={(el: any) => {
                if (item.mandatory) {
                  refArray.current[count.current] = el
                  count.current++
                }
              }}
              index={index}
            />
          </div>
        )
        break
      case BlockTypes.BlockMultimedia:
        {
          const description = item.description.replace(
            /(?:\r\n|\r|\n)/g,
            '<br>'
          )
          ;(item.description || (item.name && item.showTitle)) &&
            content.push(
              <div
                key={
                  'blockMultimediaTitle/' +
                  item.id +
                  '-' +
                  item.blockInstanceableId +
                  '-' +
                  index
                }
                className="mb-3"
              >
                {item.showTitle && (
                  <h2 className={styles.title}> {item.name}</h2>
                )}
                <div
                  className={styles.description}
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(description),
                  }}
                ></div>
              </div>
            )
          content.push(
            <div
              className="mb-4"
              key={
                'blockMultimedia/' +
                item.id +
                '-' +
                item.blockInstanceableId +
                '-' +
                index
              }
            >
              <Multimedia data={item} courseId={courseId} />
            </div>
          )
        }
        break
      case BlockTypes.BlockScorm: {
        ;(item.description || (item.name && item.showTitle)) &&
          content.push(
            <div
              key={
                'blockScormTitle/' +
                item.id +
                '-' +
                item.blockInstanceableId +
                '-' +
                index
              }
              className="mb-3"
            >
              {item.showTitle && <h2 className={styles.title}> {item.name}</h2>}
              <div
                className={styles.description}
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(
                    item.description.replace(/(?:\r\n|\r|\n)/g, '<br>')
                  ),
                }}
              />
            </div>
          )
        content.push(
          <div
            className="mb-4"
            key={
              'blockScorm/' +
              item.id +
              '-' +
              item.blockInstanceableId +
              '-' +
              index
            }
          >
            <Scorm data={item} reload={reload} next={next} />
          </div>
        )
        break
      }
      case BlockTypes.BlockEvaluation:
        {
          const { onCheck, onPreview, inProcess } = EvaluationStatesEnum
          const state =
            location.pathname.indexOf('preview') > -1
              ? onPreview
              : location.pathname.indexOf('check') > -1
              ? onCheck
              : inProcess
          const remainingAttempts =
            (item.config?.maxRetries || 0) - item.results.attemps

          content.push(
            <MandatoryBlock
              key={key}
              content={item}
              isMandatory={item.mandatory}
            />
          )
          content.push(
            PrepareEvaluation(
              item,
              t,
              location,
              setOnEvaluation,
              canEdit,
              refArray,
              count,
              lessonId
            )
          )

          state === inProcess &&
            item.results.attemps &&
            content.unshift(
              <EvaluationAlert
                key={'evaluation-alert'}
                correct={`${item.results.correct}/${item.questions?.length}`}
                approved={item.results.approved}
                points={item.results.points}
                attempts={remainingAttempts}
              />
            )
        }
        break
      default:
        break
    }
  })
  return content
}
